<template>
    <div class="bg-white rounded-md border border-gray-200 p-4 mb-3">
        <div class="mb-4">
            <div class="flex items-center justify-between mb-1">
                <div class="flex items-center gap-1.5">
                    <h2 class="text-base font-semibold text-gray-900">{{ $t('people_block.title') }}</h2>
                    <SparklesIcon class="w-4 h-4 text-royal-blue-600" :title="$t('people_block.ai_badge')" aria-label="AI" />
                    <a
                        :href="helpUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-gray-400 hover:text-royal-blue-600 transition-colors"
                        :title="$t('people_block.learn_more')"
                    >
                        <QuestionMarkCircleIcon class="w-4 h-4" />
                    </a>
                </div>
            </div>
            <p class="text-xs text-gray-500 leading-relaxed">
                {{ $t('people_block.description') }}
            </p>
        </div>

        <!-- Initial state -->
        <div v-if="viewState === 'initial'">
            <button
                @click.prevent="confirmStart"
                :disabled="loading"
                class="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-royal-blue-200 hover:bg-royal-blue-50 hover:border-royal-blue-300 text-royal-blue-700 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                    <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                    <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                    <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <path d="M9 9h.01"/>
                    <path d="M15 9h.01"/>
                </svg>
                {{ $t('people_block.find_faces') }}
            </button>
        </div>

        <!-- Scanning state -->
        <div
            v-if="viewState === 'scanning'"
            class="p-3 bg-royal-blue-50/50 border border-royal-blue-100 rounded-md"
        >
            <div class="flex justify-between items-center mb-2">
                <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-royal-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-xs font-semibold text-royal-blue-900 animate-pulse">
                        {{ $t('people_block.analyzing') }}
                    </span>
                </div>
                <span class="text-xs font-medium text-royal-blue-700">
                    {{ job?.processed_photos || 0 }} / {{ job?.total_photos || 0 }}
                </span>
            </div>
            <div class="w-full bg-royal-blue-200 h-1.5 rounded-full overflow-hidden relative">
                <div
                    class="bg-royal-blue-600 h-full rounded-full transition-all duration-500"
                    :style="`width: ${progress}%`"
                ></div>
            </div>
        </div>

        <!-- Completed state with people -->
        <div v-if="viewState === 'completed'">
            <div
                class="flex flex-wrap gap-2 gap-y-4 relative overflow-hidden transition-[max-height] duration-300 ease-in-out px-1 -mx-1 pt-1 pb-2"
                :style="facesContainerStyle"
            >
                <button
                    v-for="person in displayedPeople"
                    :key="`person-face-${person.id}`"
                    @click.prevent="selectPerson(person)"
                    class="relative group outline-none shrink-0 cursor-pointer transition-transform duration-200 hover:scale-110"
                    :title="$t('people_block.person')"
                >
                    <div
                        :class="[
                            'w-11 h-11 rounded-full overflow-hidden bg-gray-100 border-2 transition-all',
                            isPersonSelected(person)
                                ? 'border-royal-blue-500 ring-2 ring-royal-blue-100 ring-offset-1'
                                : 'border-transparent group-hover:border-royal-blue-300'
                        ]"
                    >
                        <CroppedFaceImage
                            v-if="person.cover_preview_url"
                            :src="person.cover_preview_url"
                            :bbox="person.cover_bbox"
                            :size="44"
                            :alt="$t('people_block.person')"
                            class="w-full h-full"
                        />
                        <div
                            v-else
                            class="w-full h-full flex items-center justify-center text-gray-400 text-[10px]"
                        >
                            {{ $t('people_block.no_cover') }}
                        </div>
                    </div>
                    <div
                        class="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-[9px] font-bold text-gray-700 z-10"
                    >
                        {{ person.faces_count }}
                    </div>
                </button>
            </div>

            <!-- Toggle / re-run buttons -->
            <div class="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <button
                    v-if="people.length > initialVisibleCount"
                    @click.prevent="toggleFaces"
                    class="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1 group cursor-pointer"
                >
                    <span v-if="!expanded">{{ $t('people_block.more_faces', { count: people.length - initialVisibleCount }) }}</span>
                    <span v-else>{{ $t('people_block.collapse') }}</span>
                    <ChevronDownIcon
                        class="w-3 h-3 transition-transform duration-300"
                        :class="{'rotate-180': expanded}"
                    />
                </button>
                <span v-else></span>

                <button
                    @click.prevent="confirmRerun"
                    :disabled="loading"
                    class="text-xs font-medium text-gray-400 hover:text-royal-blue-600 transition-colors flex items-center gap-1 group disabled:opacity-50 cursor-pointer"
                    :title="$t('people_block.rerun')"
                >
                    <ArrowPathIcon class="w-3 h-3 group-hover:-rotate-180 transition-transform duration-500" />
                    <span>{{ $t('people_block.rerun') }}</span>
                </button>
            </div>
        </div>

        <!-- Empty state (completed, no people found) -->
        <div
            v-if="viewState === 'empty'"
            class="mb-1 p-4 bg-gray-50 border border-gray-200 rounded-md text-center"
        >
            <div class="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-400">
                    <path d="M13.5 6.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"/>
                    <path d="M1 20a8 8 0 0 1 12.25-6.75"/>
                    <line x1="17" x2="22" y1="8" y2="13"/>
                    <line x1="22" x2="17" y1="8" y2="13"/>
                </svg>
            </div>
            <h3 class="text-sm font-semibold text-gray-900 mb-1">
                {{ $t('people_block.no_faces_found') }}
            </h3>
            <p class="text-xs text-gray-500 mb-4">
                {{ $t('people_block.no_faces_hint') }}
            </p>
            <button
                @click.prevent="confirmRerun"
                :disabled="loading"
                class="text-xs font-medium text-royal-blue-600 hover:text-royal-blue-700 transition-colors flex items-center justify-center gap-1 mx-auto group disabled:opacity-50 cursor-pointer"
            >
                <ArrowPathIcon class="w-3 h-3 group-hover:-rotate-180 transition-transform duration-500" />
                {{ $t('people_block.try_again') }}
            </button>
        </div>

        <!-- Failed state -->
        <div
            v-if="viewState === 'failed'"
            class="mb-1 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700"
        >
            <p class="mb-2">{{ $t('people_block.failed') }} <span v-if="job?.error_message">({{ job.error_message }})</span></p>
            <button
                @click.prevent="confirmRerun"
                :disabled="loading"
                class="text-xs font-medium text-red-700 hover:text-red-800 transition-colors flex items-center gap-1 cursor-pointer"
            >
                <ArrowPathIcon class="w-3 h-3" />
                {{ $t('people_block.try_again') }}
            </button>
        </div>
    </div>

    <DeleteFolderConfirmationModalComponent
        :open="pendingConfirm !== null"
        @close="pendingConfirm = null"
        @confirmAction="confirmModalAction"
        :title="pendingConfirm === 'rerun' ? $t('people_block.confirm_rerun_title') : $t('people_block.confirm_start_title')"
        :message="pendingConfirm === 'rerun' ? $t('people_block.confirm_rerun') : $t('people_block.confirm_start')"
        :button-text="pendingConfirm === 'rerun' ? $t('people_block.rerun') : $t('people_block.find_faces')"
        :variant="pendingConfirm === 'rerun' ? 'danger' : 'info'"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";

import CroppedFaceImage from "@/components/Galleries/CroppedFaceImage.vue";
import DeleteFolderConfirmationModalComponent from "@/components/Galleries/Folders/DeleteFolderConfirmationModalComponent.vue";
import {ChevronDownIcon, ArrowPathIcon, SparklesIcon} from "@heroicons/vue/20/solid";
import {QuestionMarkCircleIcon} from "@heroicons/vue/24/outline";
import {FaceGroupingStatus, FacePerson} from "@/types/FaceGroupingType";
import {ResponseError} from "@/types";

const INITIAL_VISIBLE_COUNT = 5;
const COLLAPSED_MAX_HEIGHT = '60px';
const HELP_BASE_URL = 'https://gallera.resplie.com/galleries/face-recognition';

export default defineComponent({
    name: "PeopleBlockComponent",

    components: {
        CroppedFaceImage,
        DeleteFolderConfirmationModalComponent,
        ChevronDownIcon,
        ArrowPathIcon,
        QuestionMarkCircleIcon,
        SparklesIcon,
    },

    props: {
        gallerySlug: {
            type: String,
            required: true,
        },
        selectedPerson: {
            type: [String, Number, Object] as any,
            default: null,
        },
    },

    emits: ['select-person'],

    data() {
        return {
            pollHandle: null as number | null,
            expanded: false,
            initialVisibleCount: INITIAL_VISIBLE_COUNT,
            pendingConfirm: null as 'start' | 'rerun' | null,
            startedInSession: false,
        };
    },

    computed: {
        ...mapGetters({
            job: 'faceGrouping/job',
            status: 'faceGrouping/status',
            people: 'faceGrouping/people',
            loading: 'faceGrouping/loading',
            progress: 'faceGrouping/progress',
        }),

        typedStatus(): FaceGroupingStatus {
            return this.status as FaceGroupingStatus;
        },

        isProcessing(): boolean {
            return this.typedStatus === 'queued' || this.typedStatus === 'processing';
        },

        isFailed(): boolean {
            return this.typedStatus === 'failed';
        },

        hasResults(): boolean {
            return this.typedStatus === 'completed';
        },

        viewState(): 'initial' | 'scanning' | 'completed' | 'empty' | 'failed' {
            if (this.isProcessing) return 'scanning';
            if (this.isFailed) return 'failed';
            if (this.hasResults && this.people.length === 0) return 'empty';
            if (this.hasResults && this.people.length > 0) return 'completed';
            return 'initial';
        },

        displayedPeople(): FacePerson[] {
            if (this.expanded || this.people.length <= this.initialVisibleCount) {
                return this.people;
            }
            return this.people.slice(0, this.initialVisibleCount);
        },

        facesContainerStyle(): Record<string, string> {
            if (this.expanded || this.people.length <= this.initialVisibleCount) {
                return {};
            }
            return { maxHeight: COLLAPSED_MAX_HEIGHT };
        },

        helpUrl(): string {
            return this.$i18n.locale === 'en'
                ? `${HELP_BASE_URL}?lang=en`
                : HELP_BASE_URL;
        },
    },

    mounted() {
        this.refresh();
    },

    beforeUnmount() {
        this.stopPolling();
    },

    watch: {
        gallerySlug() {
            this.startedInSession = false;
            this.refresh();
        },
        status(newStatus: FaceGroupingStatus, oldStatus: FaceGroupingStatus) {
            if (oldStatus !== 'completed' && newStatus === 'completed') {
                this.loadPeople({gallery: this.gallerySlug});
                if (this.startedInSession) {
                    this.successAlert({message: this.$t('people_block.completed'), timeout: 5});
                    this.startedInSession = false;
                }
            }
        },
    },

    methods: {
        ...mapActions({
            startFaceGrouping: 'faceGrouping/start',
            loadStatus: 'faceGrouping/loadStatus',
            loadPeople: 'faceGrouping/loadPeople',
            removeFaceGrouping: 'faceGrouping/remove',
            errorAlert: 'alerts/error',
            successAlert: 'alerts/success',
        }),

        refresh() {
            this.loadStatus({gallery: this.gallerySlug});
            this.loadPeople({gallery: this.gallerySlug});
            this.ensurePolling();
        },

        ensurePolling() {
            if (this.pollHandle !== null) return;
            this.pollHandle = window.setInterval(() => {
                if (this.isProcessing) {
                    this.loadStatus({gallery: this.gallerySlug});
                } else {
                    this.stopPolling();
                    if (this.hasResults) {
                        this.loadPeople({gallery: this.gallerySlug});
                    }
                }
            }, 5000);
        },

        stopPolling() {
            if (this.pollHandle !== null) {
                window.clearInterval(this.pollHandle);
                this.pollHandle = null;
            }
        },

        confirmStart() {
            this.pendingConfirm = 'start';
        },

        confirmRerun() {
            this.pendingConfirm = 'rerun';
        },

        confirmModalAction() {
            const action = this.pendingConfirm;
            this.pendingConfirm = null;
            if (action === 'start') {
                this.runStart();
            } else if (action === 'rerun') {
                this.removeFaceGrouping({gallery: this.gallerySlug, success: () => this.runStart()});
            }
        },

        runStart() {
            this.startedInSession = true;
            this.startFaceGrouping({
                gallery: this.gallerySlug,
                success: () => {
                    this.ensurePolling();
                },
                failed: (type: ResponseError, data: any) => {
                    this.startedInSession = false;
                    const msg = data?.message ?? 'error';
                    if (msg === 'gallery_too_large' && data?.data) {
                        this.errorAlert({
                            message: this.$t('people_block.errors.gallery_too_large', {
                                count: data.data.photos_count,
                                limit: data.data.limit,
                            }),
                            timeout: 10,
                        });
                        return;
                    }
                    const knownKey = `people_block.errors.${msg}`;
                    const translated = this.$t(knownKey);
                    this.errorAlert({
                        message: typeof translated === 'string' && translated !== knownKey ? translated : msg,
                        timeout: 10,
                    });
                },
            });
        },

        toggleFaces() {
            this.expanded = !this.expanded;
        },

        selectPerson(person: FacePerson) {
            if (this.isPersonSelected(person)) {
                this.$emit('select-person', null);
                return;
            }
            this.$emit('select-person', person);
        },

        isPersonSelected(person: FacePerson): boolean {
            return typeof this.selectedPerson === 'object' && this.selectedPerson !== null && (this.selectedPerson as FacePerson).id === person.id;
        },
    },
});
</script>
